import { v4 } from 'uuid';
import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import keyBy from 'lodash/keyBy';
import reduce from 'lodash/reduce';
import set from 'lodash/set';
import sortBy from 'lodash/sortBy';
import partition from 'lodash/partition';
import unionBy from 'lodash/unionBy';

import { generateRandomAlphanumericString } from '@xmatters/common/utils/string';
import { injectCreateAlertTriggers } from 'utils/flow/createAlert';
import { injectInitiateIncidentTriggers } from 'utils/flow/initiateIncident';
import { sanitizeMergeInputs } from 'utils/flow/merge';
import {
  edgeConnectsBlockId,
  getIntegrationsToDelete,
  isTriggerNodeType,
  isCustomHttpIntegration,
  injectSwitchCases,
  injectGenericSubtriggers,
  clearBlockPorts,
  injectConditionPaths,
} from 'utils/flow';
import {
  deserializeCreateAlertResponseOptions,
  serializeCreateAlertResponseOptions,
} from 'utils/flowBlockInput';

import {
  customScriptStepTypes,
  isFormBasedTrigger,
  scriptStepTypes,
  uniquelyLabeledStepTypes,
} from '@xmatters/common/constants/scriptSteps';
import {
  ALERT_INTEGRATION_DATA_PROP,
  CreateAlertStepInputNames,
} from 'xm-constants/toolStep';
import { getIncidentStepId } from 'xm-constants/getIncidentStep';
import { xmUpdateIncidentStep } from 'xm-constants/xmUpdateIncidentStep';

import { IntegrationOperationToTypeMap } from 'xm-constants/integration';
import mapValues from 'lodash/mapValues';
import { visitConditionPaths } from 'utils/conditionPaths';
import {
  buildIntegrationInfoMap,
  lookupTriggerInfo,
} from './triggersDefinition';

// type comes from xmapi with same value as integrationType (but type is not saved once we save integration)
// we use it in react for identify CUSTOM_HTTP. So once the type is INBOUND_WEBHOOK with valid scriptStep,
// the type should be CUSTOM_HTTP.
export const updateIntegration = integration => {
  const originalType = integration.type;
  let type = originalType;
  if (originalType === scriptStepTypes.INBOUND_WEBHOOK) {
    type = IntegrationOperationToTypeMap[integration.operation];

    if (integration.scriptStep) {
      type = type ?? scriptStepTypes.CUSTOM_HTTP;
    }
  }

  return {
    ...integration,
    type,
  };
};

function getIntegrationMapByResponseId(responses = [], integrations = []) {
  const responseIds = responses.map(r => r.id);
  return integrations.reduce((acc, integration) => {
    const responseIdReferencedByIntegration = get(
      integration,
      'responseOption.id',
    );
    if (
      responseIdReferencedByIntegration &&
      responseIds.includes(responseIdReferencedByIntegration)
    ) {
      acc[responseIdReferencedByIntegration] = integration;
    }
    return acc;
  }, {});
}

export function getResponseBlock(blocks) {
  return find(blocks, b => !b.groupingBlockId && b.step.type === 'RESPONSES');
}

function getUpdatedResponseIntegrations(
  responses,
  integrations,
  planId,
  formName,
  formId,
) {
  const updatedIntegrations = [...integrations];
  const integrationMapByResponseId = getIntegrationMapByResponseId(
    responses,
    integrations,
  );

  responses.forEach(response => {
    const integration = integrationMapByResponseId[response.id];
    const integrationName = `${formName} - ${response.text}`;

    if (integration) {
      if (integration.name !== integrationName) {
        integration.name = integrationName;
        integration.isDirty = true;
      }
    } else {
      updatedIntegrations.push({
        id: v4(),
        isDirty: true,
        type: 'OUTBOUND_WEBHOOK',
        integrationType: 'OUTBOUND_WEBHOOK',
        operation: 'RUN_SCRIPT',
        triggeredBy: 'RESPONSE',
        environment: 'HOSTED',
        deployed: true,
        plan: { id: planId },
        form: { id: formId },
        name: integrationName,
        responseOption: { id: response.id },
        createdBy: 'FLOW',
      });
    }
  });

  return updatedIntegrations;
}

export function updateResponseEdges(selectedCanvas, parentId, responseIds) {
  // convert those edges from response-option block to point to the physical port of response-trigger composite
  selectedCanvas.edges.forEach(edge => {
    const [fromBlockId] = edge.from.block.id.split('.');
    if (responseIds.includes(fromBlockId)) {
      set(edge, 'from.block.id', `${parentId}.${fromBlockId}`);
    }
  });
}

function importResponses({
  planId,
  formId,
  formName,
  selectedCanvas,
  integrations,
  responses,
}) {
  const { blocks } = selectedCanvas;
  let updatedIntegrations = integrations;

  const responseIds = responses.map(r => r.id);
  let parentTriggerBlock = getResponseBlock(blocks);
  let parentBlockId = parentTriggerBlock?.id;

  const responsesInteg =
    formId &&
    find(
      integrations,
      integ =>
        integ.triggeredBy === 'RESPONSE' &&
        integ.form?.id === formId &&
        integ.createdBy === 'FLOW',
    );
  const responsesInCanvas = parentTriggerBlock || responsesInteg;

  // either response-trigger is found in initial stage, or user has added the item manually
  if (responsesInCanvas) {
    if (!parentTriggerBlock) {
      // if it's a brand new parent-trigger, initialize its id and add it to the blocks collection
      parentTriggerBlock = {
        id: v4(),
        label: 'Responses',
        step: {
          id: null,
          type: 'RESPONSES',
        },
      };
      parentBlockId = parentTriggerBlock.id;
      blocks.push(parentTriggerBlock);
    }

    updatedIntegrations = getUpdatedResponseIntegrations(
      responses,
      integrations,
      planId,
      formName,
      formId,
    );
    const integrationMapByResponseId = getIntegrationMapByResponseId(
      responses,
      updatedIntegrations,
    );

    const blocksById = keyBy(blocks, 'id');
    responses.forEach(response => {
      const block = blocksById[response.id];
      if (!block) {
        blocks.push({
          id: response.id,
          label: response.text,
          groupingBlockId: parentBlockId,
          portType: 'responseStart',
          step: {
            id: integrationMapByResponseId[response.id].id,
            type: 'INTEGRATION',
            triggeredBy: 'response',
          },
        });
      } else {
        block.groupingBlockId = parentBlockId;
        block.label = response.text;
      }
    });

    // Used to ensure ports are sorted based on order of responses:
    const responseIdToIndex = reduce(
      responses,
      (acc, response, i) => ({ ...acc, [response.id]: i }),
      {},
    );
    const responseBlocks = filter(blocks, { groupingBlockId: parentBlockId });
    parentTriggerBlock.ports = sortBy(
      responseBlocks,
      block => responseIdToIndex[block.id],
    );
    updateResponseEdges(selectedCanvas, parentBlockId, responseIds);
  } else {
    // remove references in existing blocks and edges
    // eslint-disable-next-line no-param-reassign
    selectedCanvas.blocks = blocks.filter(b => !responseIds.includes(b.id));
    // eslint-disable-next-line no-param-reassign
    selectedCanvas.edges = selectedCanvas.edges.filter(e => {
      const sourceId = get(e, 'from.block.id');
      return responseIds.find(id => sourceId.indexOf(id) > -1) == null;
    });
  }

  return [selectedCanvas, updatedIntegrations];
}

const hasCreateAlertIntegrationData = block =>
  get(block, ['step', ALERT_INTEGRATION_DATA_PROP]);

/**
 * Remove any blocks referencing missing integration, and
 * Add integration details to integration blocks.
 *
 * The following fields are added:
 * - triggeredBy
 * - type (set to null if the integration is INBOUND)
 *
 * @param canvas
 * @param integrations
 * @returns A copy of the canvas with augmented integration blocks.
 */
export function expandIntegrationBlocks(canvas, integrations = []) {
  const triggerInfoLookup = buildIntegrationInfoMap(integrations);
  const integrationMapById = integrations.reduce((acc, i) => {
    acc[i.id] = i;
    return acc;
  }, {});

  // Custom integrations are not by step id since one step may have many integrations.
  // Default outbound triggers are held by step id though...
  const getIntegration = b =>
    integrationMapById[b.id] || integrationMapById[get(b, 'step.id')];

  return {
    ...canvas,
    triggerInfoLookup,
    blocks: (canvas.blocks || [])
      .filter(b => {
        const stepReferenceId = get(b, 'step.id');
        const scriptStepType = get(b, 'step.type');
        const integration = getIntegration(b);

        return (
          !stepReferenceId ||
          scriptStepType !== 'INTEGRATION' ||
          integration ||
          hasCreateAlertIntegrationData(b)
        );
      })
      .map(b => {
        if (b.step.type === 'RESPONSES') {
          return {
            ...b,
            step: {
              ...b.step,
              triggeredBy: 'RESPONSE',
            },
          };
        }

        const stepReferenceId = get(b, 'step.id');
        const scriptStepType = get(b, 'step.type');

        const integration = getIntegration(b);
        const triggerInfo = triggerInfoLookup[stepReferenceId] || {};
        if (
          scriptStepType !== 'INTEGRATION' ||
          hasCreateAlertIntegrationData(b)
        ) {
          return { ...b, step: { ...triggerInfo, ...b.step } };
        }

        // TODO: not this. Allow other types of INBOUND_WEBHOOK in the future. To do that we
        // will need to combine the `custom_steps` in here, would be much simpler after move to selectors.
        let { type } = triggerInfo;
        if (!type && integration.integrationType === 'INBOUND_WEBHOOK') {
          type = 'CUSTOM_HTTP';
        }

        return {
          ...b,
          step: {
            ...triggerInfo,
            ...b.step,
            triggeredBy: integration.triggeredBy,
            type,
          },
        };
      }),
  };
}

function removeOrphanedGroupedBlocks(selectedCanvas) {
  const { blocks } = selectedCanvas;
  const blocksById = keyBy(blocks, 'id');
  const blocksWithoutGroupOrphans = filter(
    blocks,
    ({ groupingBlockId }) => !groupingBlockId || blocksById[groupingBlockId],
  );

  // Length check is sufficient only because this method strictly _removes_; does not add or modify.
  // If ever that changes: A) change the method name, and B) perform a more robust check here.
  if (blocksWithoutGroupOrphans.length < blocks.length) {
    /* eslint-disable no-param-reassign */
    selectedCanvas.blocks = blocksWithoutGroupOrphans;
    /* eslint-enable no-param-reassign */
  }
}

export function buildPlanCanvasAndIntegration(data) {
  const {
    planCanvas,
    customSteps,
    allCanvases,
    planId,
    canvasId,
    blocks,
    edges,
    inputs,
    uiIntegrations: integrations,
    responses,
    switchCases,
    mergeOutputs,
  } = data;
  let updatedIntegrations = integrations;

  const canvas = planCanvas || allCanvases.filter(c => c.id === canvasId)[0];
  const defaultValues = {
    name: canvas.name,
    plan: { id: planId },
    form: canvas.form,
  };
  let selectedCanvas = {
    ...defaultValues,
    ...(planCanvas || {}),
    conditionPathsByBlockId: planCanvas.conditionPathsByBlockId ?? {},
    incidentProperties: planCanvas.incidentProperties ?? {},
    blocks: blocks || [],
    edges: edges || [],
    mergeOutputs: mergeOutputs || [],
  };

  selectedCanvas.switchCases = switchCases || selectedCanvas.switchCases;
  selectedCanvas.inputs = inputs || selectedCanvas.inputs;

  const existingBlockStepIds = selectedCanvas.blocks.map(
    block => block.step.id,
  );
  const existingPortsByBlockId = mapValues(
    keyBy(selectedCanvas.blocks, 'id'),
    'ports',
  );

  clearBlockPorts(selectedCanvas);
  injectGenericSubtriggers({
    blocks: selectedCanvas.blocks,
    edges: selectedCanvas.edges,
    existingPortsByBlockId,
  }); // Note: block.ports will be overwritten by the more specialized inject calls below
  injectSwitchCases(selectedCanvas);
  injectCreateAlertTriggers(selectedCanvas, planId);
  injectInitiateIncidentTriggers(
    selectedCanvas,
    planId,
    customSteps,
    integrations,
  );
  injectConditionPaths(selectedCanvas, existingPortsByBlockId);

  removeOrphanedGroupedBlocks(selectedCanvas);

  // Add legacy integrations:
  let y = 0;
  integrations
    .filter(
      i =>
        i.type !== 'INBOUND_WEBHOOK' &&
        selectedCanvas.form &&
        i.form &&
        i.form.id === selectedCanvas.form.id &&
        !existingBlockStepIds.includes(i.id),
    )
    .forEach(integ => {
      if (integ.triggeredBy === 'RESPONSE' && integ.createdBy === 'FLOW') {
        // Handled separately in importResponses for now
        return;
      }

      // Deconstruct xm-api data objects:
      const agents = integ.agents?.data ?? integ.agents;
      selectedCanvas.blocks.push({
        id: v4(),
        label: integ.name,
        step: {
          id: integ.id,
          type: 'INTEGRATION',
        },
        endpoint: integ.endpoint,
        presentation: {
          x: 0,
          y,
          w: 100,
          h: 50,
        },
        agents,
        isGeneratedForLegacyIntegration: true,
      });
      y += 170;
    });

  if (selectedCanvas.form) {
    [selectedCanvas, updatedIntegrations] = importResponses({
      planId,
      formId: selectedCanvas.form.id,
      formName: selectedCanvas.form.name,
      selectedCanvas,
      integrations: updatedIntegrations.map(integration =>
        updateIntegration(integration),
      ),
      responses,
    });
  }
  selectedCanvas = expandIntegrationBlocks(selectedCanvas, updatedIntegrations);
  selectedCanvas = sanitizeMergeInputs(selectedCanvas);

  return [selectedCanvas, updatedIntegrations];
}

export function buildPlanCanvas(data) {
  const [selectedCanvas] = buildPlanCanvasAndIntegration(data);
  return selectedCanvas;
}

function isTrigger({ integrationType }) {
  return isTriggerNodeType(integrationType);
}

const triggersUtilitySteps = {
  '79444b3e-e890-5dab-a066-f1cc7d09c210': true,
  '6419e9be-78ef-56b6-92e2-869495d5b959': true,
};

const isTriggersUtilityStep = step => triggersUtilitySteps[step.id];

function getStepIntegrationType(scriptStepType) {
  switch (scriptStepType) {
    case scriptStepTypes.TEMPLATE_EBFD_LAUNCH_INCIDENT_STEP:
    case scriptStepTypes.EBFD_LAUNCH_INCIDENT_STEP:
    case scriptStepTypes.TEMPLATE_EBFD_UPDATE_INCIDENT_STEP:
    case scriptStepTypes.EBFD_UPDATE_INCIDENT_STEP:
    case scriptStepTypes.TEMPLATE_EBFD_CLOSE_INCIDENT_STEP_V2:
    case scriptStepTypes.EBFD_CLOSE_INCIDENT_STEP_V2:
      return scriptStepType;
    default:
      return isTrigger({ integrationType: scriptStepType })
        ? scriptStepTypes.CUSTOM_HTTP
        : scriptStepTypes.SCRIPT_STEP;
  }
}

export function transformStepData(step) {
  const isTriggerByIntegrationType = isTrigger({
    integrationType: step.scriptStepType,
  });
  return {
    ...step,
    uuid: step.id,
    integrationType: getStepIntegrationType(step.scriptStepType),
    isBuiltIn: !customScriptStepTypes.has(step.scriptStepType),
    isTrigger: isTriggerByIntegrationType,
    workflow: null,
    unsavedUsage: step.usage,
    paletteCategory: isTriggersUtilityStep(step) ? 'TRIGGER_UTILITY' : '',
  };
}

export const transformStepsData = data => data.map(transformStepData);

export function transformBlockData(block) {
  const { agents } = block;
  if (!(agents && agents.data)) {
    return block;
  }

  return {
    ...block,
    agents: agents.data.map(a => ({ id: a.id })),
  };
}

export const transformBlocksData = data => data?.map(transformBlockData) ?? [];

// If valueGetter is anything other than a function, it will be added as is rather than run:
export function upsertById(collection, id, valueGetter) {
  const index = findIndex(collection, { id });
  const newValue = isFunction(valueGetter)
    ? valueGetter(collection[index], index)
    : valueGetter;

  if (index === -1) {
    return [...collection, newValue];
  }

  const result = [...collection];
  result[index] = newValue;
  return result;
}

/** toRemove: id: the identifier, text: the type. If the type is response, we want to delete all specific response integrations */
export function removeIntegrations(integrations, toRemove) {
  return integrations.filter(integration => integration.id !== toRemove);
}

export function getRemovedIntegrationsState({
  blocksToRemove,
  getUpdatedCanvas,
  state,
}) {
  let { modifiedForms } = state;
  const { planCanvas, forms } = state;
  const { edges = [], blocks = [], inputs = [] } = planCanvas ?? {};
  let nextEdges = [...edges];
  let nextBlocks = [...blocks];
  let nextInputs = [...inputs];
  let uiIntegrations = [...state.uiIntegrations];

  blocksToRemove.forEach(block => {
    const blockId = block.id;
    const integrationToRemove = getIntegrationsToDelete(
      block,
      uiIntegrations,
    )[0];

    if (integrationToRemove) {
      uiIntegrations = removeIntegrations(
        uiIntegrations,
        integrationToRemove.id,
      );
      if (
        integrationToRemove.form &&
        integrationToRemove.operation === 'TRIGGER_INCIDENT'
      ) {
        const withdrawnForm = find(forms, { id: integrationToRemove.form.id });
        if (withdrawnForm) {
          const form = {
            ...withdrawnForm,
            uiEnabled: false,
            mobileEnabled: false,
          };
          modifiedForms = upsertById(modifiedForms, form.id, form);
        }
      }
    }
    nextEdges = nextEdges.filter(e => !edgeConnectsBlockId(e, blockId));
    nextBlocks = nextBlocks.filter(b => b.id !== blockId);
    nextInputs = nextInputs.filter(i => i.flowBlock.id !== blockId);
  });

  // Update canvas and return updated reducer state:
  const updatedCanvas = getUpdatedCanvas({
    planCanvas: {
      ...state.planCanvas,
      blocks: nextBlocks,
      edges: nextEdges,
      inputs: nextInputs,
    },
    blocks: nextBlocks,
    edges: nextEdges,
    inputs: nextInputs,
    uiIntegrations,
  });
  return {
    ...state,
    uiIntegrations,
    planCanvas: updatedCanvas,
    modifiedForms,
    blocks: updatedCanvas.blocks,
    edges: updatedCanvas.edges,
    inputs: updatedCanvas.inputs,
  };
}

/**
 * Build an object with integration id as key and trigger information as value
 * @param integrations list of all integrations
 */
function buildTriggersByTriggerId(integrations) {
  return integrations.filter(isTrigger).reduce((acc, integration) => {
    const {
      deployed,
      id,
      integrationType,
      triggeredBy,
      type: stepType,
      operation,
    } = integration;
    const trigger = acc[id];
    if (trigger) {
      return acc;
    }

    const type = triggeredBy || operation || stepType || integrationType;
    const isCustomHttp = isCustomHttpIntegration(integration);

    const defaultTriggerObj = lookupTriggerInfo(integration);
    const scriptStepId =
      get(integration, 'scriptStep.id') ||
      (defaultTriggerObj && defaultTriggerObj.uuid);

    acc[id] = {
      scriptStepId,
      id,
      type,
      isDeployed: deployed,
      isCustomHttp,
    };
    return acc;
  }, {});
}

function addTrigger(trigger, triggers) {
  if (trigger && triggers && triggers.findIndex(t => t.id === trigger.id) < 0) {
    triggers.push(trigger);
  }
}

/**
 * Build an object with canvas id as key and list of trigger information as value
 * @param canvases list of all canvases
 * @param integrations list of all integrations
 */
function buildTriggerByCanvasIdMap(canvases, integrations) {
  const triggersByCanvasId = buildTriggersByTriggerId(integrations);

  return reduce(
    canvases,
    (triggersByCanvasIdMap, canvas) => {
      const blocks = canvas.blocks || [];
      const canvasTriggers = triggersByCanvasIdMap[canvas.id] || [];
      const triggers = [];

      blocks.forEach(block => {
        const addedTrigger = canvasTriggers.find(
          t => t.scriptStepId === block.step.id,
        );
        if (!addedTrigger) {
          addTrigger(triggersByCanvasId[block.step.id], triggers);
        }
      });
      // for integrations that is not in triggers yet (since the block for it is not saved to the canvas yet)
      if (canvas.form) {
        const integrationWithForm =
          filter(
            integrations,
            integration =>
              integration.form &&
              integration.form.id === canvas.form.id &&
              !isFormBasedTrigger(integration.type),
          ) || [];
        if (integrationWithForm.length > 0) {
          integrationWithForm.forEach(integration => {
            addTrigger(triggersByCanvasId[integration.id], triggers);
          });
        }
      }
      return {
        ...triggersByCanvasIdMap,
        [canvas.id]: triggers,
      };
    },
    {},
  );
}

function buildFormCanvasMap(canvases) {
  const canvasWithForm = (canvases || []).filter(c => c.form && c.form.id);
  return reduce(
    canvasWithForm,
    (acc, c) => ({
      ...acc,
      [c.form.id]: c,
    }),
    {},
  );
}

/**
 * Build a list of flows and triggers information
 * @param forms list of all forms
 * @param canvases list of all canvases
 * @param integrations list of all integrations for each form
 */

export function buildFlowList(forms, canvases, integrations) {
  const formCanvasMap = buildFormCanvasMap(canvases);
  const formWithoutCanvas = filter(forms, f => !formCanvasMap[f.id]) || [];
  if (formWithoutCanvas && formWithoutCanvas.length > 0) {
    // add new one to all canvas so that we get canvasId
    formWithoutCanvas.forEach(form => {
      const canvasId = v4();
      canvases.push({
        id: canvasId,
        name: form.name,
        form: { id: form.id, name: form.name },
        plan: form.plan,
        initial: true,
      });
    });
  }
  const triggersByCanvasId = buildTriggerByCanvasIdMap(canvases, integrations);

  return (
    canvases.map(canvas => ({
      canvas,
      canvasName: canvas.name,
      canvasId: canvas.id,
      triggers: triggersByCanvasId[canvas.id] || [],
      hasFlow: !isEmpty(triggersByCanvasId[canvas.id]),
    })) || []
  );
}

export const makeFlowBlockInput = (blockId, scriptStepInput, defaultValue) => {
  const value = defaultValue ? { value: defaultValue } : {};
  return {
    flowBlock: { id: blockId },
    scriptStepInput,
    xagents: [],
    ...value,
  };
};

export const duplicateBlock = ({
  block,
  stepType,
  xOffset,
  yOffset,
  blockLabelBuilder,
  intl,
}) => {
  const isBlockLabelSupposedToBeUnique = uniquelyLabeledStepTypes.has(stepType);
  const duplicatedBlockId = v4();

  return {
    ...cloneDeep(block),
    isDuplicateHighlightVisible: true,
    id: duplicatedBlockId,
    step: {
      ...block.step,
      type: stepType,
    },
    label: isBlockLabelSupposedToBeUnique
      ? blockLabelBuilder({
          type: stepType,
          label: intl.formatMessage(
            { id: 'COPY_OF_NAME' },
            { name: block.label },
          ),
        })
      : block.label,
    presentation: {
      ...block.presentation,
      x: block.presentation.x + xOffset,
      y: block.presentation.y + yOffset,
    },
  };
};

export const duplicateBlockEndpoint = ({
  endpoints,
  duplicatedBlockId,
  blockId,
}) => {
  const blockEndpoint = endpoints.find(
    endpoint => endpoint.flowBlockId === blockId,
  );
  if (!blockEndpoint) {
    return undefined;
  }

  const duplicatedEndpoint = {
    ...cloneDeep(blockEndpoint),
    flowBlockId: duplicatedBlockId,
  };

  return duplicatedEndpoint;
};

export const duplicateSwitchCases = ({
  switchCases,
  blockId,
  duplicatedBlockId,
  stepType,
}) => {
  if (stepType !== scriptStepTypes.SWITCH) {
    return undefined;
  }

  const originalSwitchCases = switchCases.filter(
    switchCase => switchCase.block.id === blockId,
  );

  if (originalSwitchCases.length === 0) {
    return undefined;
  }

  return originalSwitchCases.map(switchCase => ({
    ...cloneDeep(switchCase),
    id: v4(),
    block: {
      id: duplicatedBlockId,
    },
  }));
};

export const duplicateConditionPaths = ({
  conditionPathsByBlockId,
  blockId,
}) => {
  const conditionPaths = conditionPathsByBlockId[blockId];
  if (!conditionPaths) {
    return undefined;
  }

  const newConditionPaths = cloneDeep(conditionPaths);
  visitConditionPaths({
    conditionPaths: newConditionPaths,
    visitConditionPath: conditionPath => {
      // eslint-disable-next-line no-param-reassign
      conditionPath.id = v4();
    },
    visitCondition: condition => {
      // eslint-disable-next-line no-param-reassign
      condition.leftInputId = v4();
      // eslint-disable-next-line no-param-reassign
      condition.rightInputId = v4();
      // eslint-disable-next-line no-param-reassign
      condition.id = v4();
    },
  });

  return newConditionPaths;
};

export const duplicateMergeStepOutputs = ({
  mergeOutputs,
  blockId,
  duplicatedBlockId,
  stepType,
}) => {
  if (stepType !== scriptStepTypes.MERGE) {
    return undefined;
  }

  const originalMergeStepOutputs = mergeOutputs.filter(
    mergeOutput => mergeOutput.block.id === blockId,
  );

  if (originalMergeStepOutputs.length === 0) {
    return undefined;
  }

  return originalMergeStepOutputs.map(mergeOutput => ({
    ...cloneDeep(mergeOutput),
    id: v4(),
    block: {
      id: duplicatedBlockId,
    },
  }));
};

export const duplicateIncidentProperties = ({
  incidentProperties,
  blockId,
  duplicatedBlockId,
  block,
}) => {
  const stepIds = [getIncidentStepId, xmUpdateIncidentStep];

  if (stepIds.includes(block.step.id)) {
    const orginalStepProperties = incidentProperties[blockId];

    return orginalStepProperties.map(property => ({
      ...cloneDeep(property),
      flowBlock: {
        id: duplicatedBlockId,
      },
      id: v4(),
    }));
  }
  return undefined;
};

export const duplicateInputs = ({
  inputs,
  blockId,
  duplicatedBlockId,
  stepType,
}) => {
  if (stepType === scriptStepTypes.MERGE) {
    return undefined;
  }

  const blockInputs = inputs.filter(input => input.flowBlock.id === blockId);
  const duplicatedBlockInputs = blockInputs.map(input => ({
    ...cloneDeep(input),
    id: v4(),
    flowBlock: {
      ...input.block,
      id: duplicatedBlockId,
    },
  }));

  // duplicated Create Alert blocks should not have their subtriggers duplicated
  if (stepType === scriptStepTypes.CREATE_ALERT) {
    const triggersInput = duplicatedBlockInputs.find(
      input =>
        input.scriptStepInput?.builtInType === scriptStepTypes.CREATE_ALERT &&
        input.scriptStepInput?.name === CreateAlertStepInputNames.TRIGGERS,
    );
    triggersInput.value = '';

    const responseOptionsInput = duplicatedBlockInputs.find(
      input =>
        input.scriptStepInput?.builtInType === scriptStepTypes.CREATE_ALERT &&
        input.scriptStepInput?.name === CreateAlertStepInputNames.RESPONSES,
    );
    if (responseOptionsInput) {
      const responseValue = deserializeCreateAlertResponseOptions(
        responseOptionsInput.value,
      );
      const updatedResponseValue = responseValue.map(response => ({
        ...response,
        id: v4(),
        integrationId: v4(),
        triggerBlockId: v4(),
      }));
      responseOptionsInput.value =
        serializeCreateAlertResponseOptions(updatedResponseValue);
    }
  }

  return duplicatedBlockInputs;
};

export const duplicateIntegration = ({ integrations, stepId, stepType }) => {
  const originalIntegration = integrations.find(
    integration => integration.id === stepId,
  );

  if (!originalIntegration) {
    return undefined;
  }

  const duplicatedIntegrationId = v4();
  const name = originalIntegration.name.replace(
    ` - ${originalIntegration.id}`,
    '',
  );
  const duplicatedIntegration = {
    ...cloneDeep(originalIntegration),
    id: duplicatedIntegrationId,
    name: `${name} - ${duplicatedIntegrationId}`,
  };

  if (stepType === scriptStepTypes.INBOUND_EMAIL) {
    duplicatedIntegration.triggerId = generateRandomAlphanumericString(5);
  }

  const { triggerFilter } = originalIntegration;
  if (triggerFilter) {
    duplicatedIntegration.triggerFilter = {
      ...cloneDeep(triggerFilter),
      id: v4(),
      trigger: duplicatedIntegrationId,
      workflow: duplicatedIntegration.plan.id,
    };
  }

  return duplicatedIntegration;
};

// When a user moves Block to the canvas, step.unsavedUsage needs to be updated
export const addUnsavedUsageToCustomSteps = (canvas, customSteps, stepId) => {
  const newUsage = {
    appName: canvas?.plan?.name,
    canvasName: canvas?.name,
    canvasUuid: canvas?.id,
    formName: canvas?.form?.name,
    formUuid: canvas?.form?.id,
  };

  const [foundSteps, otherSteps] = partition(
    customSteps,
    cs => cs.id === stepId,
  );

  const updatedSteps = foundSteps.map(item => {
    return {
      ...item,
      unsavedUsage: unionBy(item?.unsavedUsage, [newUsage], 'canvasUuid'),
    };
  });

  return [...otherSteps, ...updatedSteps];
};

// When a user removes Block from the canvas, step.unsavedUsage needs to be updated
export const removeUnsavedUsageInCustomSteps = (
  blocks,
  canvasId,
  customSteps,
  stepId,
) => {
  const [foundSteps, otherSteps] = partition(
    customSteps,
    cs =>
      cs.id === stepId &&
      cs?.unsavedUsage?.some(item => item?.canvasUuid === canvasId),
  );

  // Before removing usage we need to check that this step isn't used on the canvas by another block
  const checkIfBlockOnCanvasUsingStep = blocks.some(
    b => b?.step?.id === stepId,
  );

  const updatedSteps = checkIfBlockOnCanvasUsingStep
    ? foundSteps
    : foundSteps.map(item => {
        return {
          ...item,
          unsavedUsage: item?.unsavedUsage.filter(
            u => u?.canvasUuid !== canvasId,
          ),
        };
      });
  return [...otherSteps, ...updatedSteps];
};

// When a user clicks 'Save' button, step.usage needs to be override by step.unsavedUsage
export const mergeUnsavedUsageInCustomSteps = customSteps => {
  const updatedSteps = customSteps.map(item => {
    if (item?.unsavedUsage) {
      return {
        ...item,
        usage: item?.unsavedUsage,
      };
    }
    return item;
  });

  return updatedSteps;
};

export const getIncidentPropertiesByBlockId = dynamicOutputs => {
  const allIncidentProperties = dynamicOutputs.filter(item =>
    [getIncidentStepId, xmUpdateIncidentStep].includes(item.scriptStep.id),
  );

  return groupBy(allIncidentProperties, 'flowBlockId');
};
